import { useMemo } from 'react';

import { RunSchema } from '@/types/schema';
import { enumerateKeys } from '@/utils/enumerate-keys';
import { cn } from '@/utils/tailwind';

import { ConversationPathConfigurator } from './ConversationPathConfigurator';
import { ConversationTurnV2 } from './ConversationTurnV2';

interface ConversationViewConfiguratorProps {
  runs: RunSchema[];
  selectedInputPaths: string[];
  selectedOutputPaths: string[];
  onChangeInputPaths: (paths: string[]) => void;
  onChangeOutputPaths: (paths: string[]) => void;
}

function ensureTraversable(
  value: unknown
): Record<string, unknown> | Array<unknown> {
  if (value == null) {
    return {};
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'object') {
    return value as Record<string, unknown>;
  }
  return { value };
}

export function ConversationViewConfigurator({
  runs,
  selectedInputPaths,
  selectedOutputPaths,
  onChangeInputPaths,
  onChangeOutputPaths,
}: ConversationViewConfiguratorProps) {
  const previewRun = runs.length > 0 ? runs[runs.length - 1] : null;
  const availableInputPaths = useMemo(() => {
    const pathSet = new Set<string>();

    const traversable = ensureTraversable(previewRun?.inputs);
    enumerateKeys({
      obj: traversable as Record<string, unknown>,
      prefix: undefined,
      includeNegativeIndices: true,
      includeWildcard: false,
    })
      .filter((path) => path !== '')
      .forEach((path) => pathSet.add(path));

    return Array.from(pathSet);
  }, [previewRun]);

  const availableOutputPaths = useMemo(() => {
    const pathSet = new Set<string>();

    const traversable = ensureTraversable(previewRun?.outputs);
    enumerateKeys({
      obj: traversable as Record<string, unknown>,
      prefix: undefined,
      includeNegativeIndices: true,
      includeWildcard: false,
    })
      .filter((path) => path !== '')
      .forEach((path) => pathSet.add(path));

    return Array.from(pathSet);
  }, [previewRun]);

  const plainOptions = {
    input:
      selectedInputPaths.length > 0
        ? { type: 'key' as const, value: selectedInputPaths }
        : { type: 'key-heuristic' as const },
    output:
      selectedOutputPaths.length > 0
        ? { type: 'key' as const, value: selectedOutputPaths }
        : { type: 'key-heuristic' as const },
  };

  return (
    <div className={cn('flex h-[calc(100vh-77px)] divide-x divide-secondary')}>
      <div className="h-full flex-1 overflow-auto bg-secondary">
        {runs.map((turn, index) => (
          <ConversationTurnV2
            selectedRunId={null}
            key={turn.id}
            run={turn}
            idx={index}
            plainOptions={plainOptions}
            rootRuns={runs}
          />
        ))}
      </div>

      <div className="flex flex-1 flex-col overflow-hidden">
        <ConversationPathConfigurator
          availableInputPaths={availableInputPaths}
          availableOutputPaths={availableOutputPaths}
          inputValues={previewRun?.inputs}
          outputValues={previewRun?.outputs}
          selectedInputPaths={selectedInputPaths}
          selectedOutputPaths={selectedOutputPaths}
          onChangeInputPaths={onChangeInputPaths}
          onChangeOutputPaths={onChangeOutputPaths}
        />
      </div>
    </div>
  );
}
